import { memo, useCallback, useState } from "react";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";
import { useHotelApplicationDataQuery } from "@/components/features/_shared/repository/hotel-application-data/query";

import { useFetchHotelTop } from "@/components/features-new/hotel-top/api/fetch";

import { CompletionDialog } from "./completion-dialog";
import { Step1 } from "./step-1";
import { Step2, STEP2_ITEM_COUNT } from "./step-2";
import { Step3, STEP3_ITEM_COUNT } from "./step-3";

type Props = {
	hotelID: string;
};

const TOTAL_ITEMS = STEP2_ITEM_COUNT + STEP3_ITEM_COUNT;

const STORAGE_KEY_PREFIX = "start-guide-checklist-v2";

const getStorageKey = (hotelID: string) => `${STORAGE_KEY_PREFIX}-${hotelID}`;

/**
 * チェックリストの内容は、遷移されても保持したいため、ローカルストレージで管理
 */
const loadCheckedItems = (hotelID: string): boolean[] => {
	const stored = localStorage.getItem(getStorageKey(hotelID));
	if (stored) {
		const parsed = JSON.parse(stored) as boolean[];
		if (Array.isArray(parsed) && parsed.length === TOTAL_ITEMS) {
			return parsed;
		}
	}
	return new Array(TOTAL_ITEMS).fill(false);
};

export const StartGuideChecklist = memo(({ hotelID }: Props) => {
	const { hotelApplicationData } = useHotelApplicationDataQuery(hotelID);
	const isTestReservationAvailable = hotelApplicationData?.hotelApplicationStatus.isTestReservationAvailable ?? false;

	const { data: hotelTopData } = useFetchHotelTop({ hotelID });

	const [checkedItems, setCheckedItems] = useState<boolean[]>(() => loadCheckedItems(hotelID));
	const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);

	const handleCheckedChange = useCallback(
		(globalIndex: number, checked: boolean) => {
			setCheckedItems((prev) => {
				const next = [...prev];
				next[globalIndex] = checked;
				localStorage.setItem(getStorageKey(hotelID), JSON.stringify(next));
				if (next.every(Boolean)) {
					setIsCompletionDialogOpen(true);
				}
				return next;
			});
		},
		[hotelID],
	);

	const step2CheckedItems = checkedItems.slice(0, STEP2_ITEM_COUNT);
	const step3CheckedItems = checkedItems.slice(STEP2_ITEM_COUNT);

	const handleStep2CheckedChange = useCallback(
		(index: number, checked: boolean) => {
			handleCheckedChange(index, checked);
		},
		[handleCheckedChange],
	);

	const handleStep3CheckedChange = useCallback(
		(index: number, checked: boolean) => {
			handleCheckedChange(STEP2_ITEM_COUNT + index, checked);
		},
		[handleCheckedChange],
	);

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader title="販売前最終チェック表" />
			<FeatureIslandMainWithBackGround>
				<div className="space-y-8">
					<Step1 hotelID={hotelID} isTestReservationAvailable={isTestReservationAvailable} />
					<Step2 checkedItems={step2CheckedItems} onCheckedChange={handleStep2CheckedChange} />
					<Step3 checkedItems={step3CheckedItems} onCheckedChange={handleStep3CheckedChange} />
				</div>
			</FeatureIslandMainWithBackGround>

			{hotelTopData && (
				<CompletionDialog
					open={isCompletionDialogOpen}
					onOpenChange={setIsCompletionDialogOpen}
					hotelUrls={hotelTopData.hotelUrls}
				/>
			)}
		</FeatureIslandContainer>
	);
});

StartGuideChecklist.displayName = "StartGuideChecklist";
